import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MessageSquare,
  Plus,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronRight,
  Filter,
  RefreshCw,
  Bug,
  HelpCircle,
  Lightbulb,
  MoreHorizontal
} from 'lucide-react';
import TicketCreateModal from './TicketCreateModal';
import TicketDetail from './TicketDetail';
import './Tickets.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const STATUS_CONFIG = {
  IN_PROGRESS: { label: 'En cours', color: '#f59e0b', icon: Clock, bgColor: '#fffbeb' },
  RESOLVED: { label: 'Résolu', color: '#10b981', icon: CheckCircle, bgColor: '#ecfdf5' },
  CLOSED: { label: 'Fermé', color: '#6b7280', icon: XCircle, bgColor: '#f3f4f6' }
};

const USER_FILTER_STATUSES = ['IN_PROGRESS', 'RESOLVED', 'CLOSED'];

const CATEGORY_CONFIG = {
  BUG: { label: 'Bug', icon: Bug, color: '#ef4444' },
  QUESTION: { label: 'Question', icon: HelpCircle, color: '#3b82f6' },
  SUGGESTION: { label: 'Suggestion', icon: Lightbulb, color: '#f59e0b' },
  OTHER: { label: 'Autre', icon: MoreHorizontal, color: '#6b7280' }
};

const PRIORITY_CONFIG = {
  LOW: { label: 'Basse', color: '#6b7280' },
  MEDIUM: { label: 'Moyenne', color: '#3b82f6' },
  HIGH: { label: 'Haute', color: '#f59e0b' },
  CRITICAL: { label: 'Critique', color: '#ef4444' }
};

function TicketsView({
  token,
  user,
  userSubscription,
  currentView,
  previousView,
  selectedClientId,
  selectedSiteId,
  currentDashboardType,
  globalConnectors,
  userOrganizations,
  clients,
  initialTicketId = null,
  onTicketSelect
}) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;

      const response = await axios.get(`${API_URL}/api/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setTickets(response.data.tickets);
        setPagination(response.data.pagination);
      }
      setError(null);
    } catch (err) {
      console.error('Erreur chargement tickets:', err);
      setError('Impossible de charger les tickets');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, pagination.page]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Charger et sélectionner un ticket spécifique depuis l'URL (lien email)
  useEffect(() => {
    const loadInitialTicket = async () => {
      if (initialTicketId && !selectedTicket) {
        try {
          const response = await axios.get(`${API_URL}/api/tickets/${initialTicketId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
            setSelectedTicket(response.data.ticket);
            onTicketSelect?.(response.data.ticket.id);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du ticket:', error);
        }
      }
    };
    loadInitialTicket();
  }, [initialTicketId, token, selectedTicket, onTicketSelect]);

  const handleTicketCreated = (newTicket) => {
    setTickets(prev => [newTicket, ...prev]);
    setShowCreateModal(false);
  };

  const handleTicketUpdated = (updatedTicket) => {
    setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (selectedTicket) {
    return (
      <div className="tickets-view tickets-view-detail">
        <TicketDetail
          ticket={selectedTicket}
          token={token}
          user={user}
          onBack={() => {
            setSelectedTicket(null);
            onTicketSelect?.(null);
          }}
          onTicketUpdated={handleTicketUpdated}
        />
      </div>
    );
  }

  return (
    <div className="tickets-view">
      <div className="tickets-header">
        <div className="tickets-header-left">
          <MessageSquare size={24} />
          <h1>Support</h1>
          <span className="tickets-count">{pagination.total} ticket{pagination.total !== 1 ? 's' : ''}</span>
        </div>
        <div className="tickets-header-right">
          <button
            className="tickets-refresh-btn"
            onClick={fetchTickets}
            disabled={loading}
          >
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
          <button
            className="tickets-create-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={18} />
            Nouveau ticket
          </button>
        </div>
      </div>

      <div className="tickets-filters">
        <div className="tickets-filter-group">
          <Filter size={16} />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">Tous les statuts</option>
            {USER_FILTER_STATUSES.map(key => (
              <option key={key} value={key}>{STATUS_CONFIG[key].label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="tickets-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {loading && tickets.length === 0 ? (
        <div className="tickets-loading">
          <RefreshCw size={24} className="spinning" />
          <span>Chargement des tickets...</span>
        </div>
      ) : tickets.length === 0 ? (
        <div className="tickets-empty">
          <MessageSquare size={48} />
          <h3>Aucun ticket</h3>
          <p>Vous n'avez pas encore créé de ticket de support.</p>
          <button onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Créer un ticket
          </button>
        </div>
      ) : (
        <div className="tickets-list tickets-list-compact">
          {tickets.map(ticket => {
            const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.IN_PROGRESS;
            const categoryConfig = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.QUESTION;
            const StatusIcon = statusConfig.icon;
            const CategoryIcon = categoryConfig.icon;

            return (
              <div
                key={ticket.id}
                className="ticket-row"
                onClick={() => {
                  setSelectedTicket(ticket);
                  onTicketSelect?.(ticket.id);
                }}
              >
                <span className="ticket-row-number">#{String(ticket.number).padStart(4, '0')}</span>
                <span className="ticket-row-subject">{ticket.subject}</span>
                <span className="ticket-row-email">{ticket.user?.email || '-'}</span>
                <span className="ticket-row-plan">{ticket.context?.userPlan || '-'}</span>
                <span className="ticket-row-category" style={{ color: categoryConfig.color }}>
                  <CategoryIcon size={14} />
                  {categoryConfig.label}
                </span>
                <span
                  className="ticket-row-status"
                  style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                >
                  <StatusIcon size={12} />
                  {statusConfig.label}
                </span>
                <span className="ticket-row-date">{formatDate(ticket.updatedAt || ticket.createdAt)}</span>
                <ChevronRight size={16} className="ticket-row-chevron" />
              </div>
            );
          })}
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="tickets-pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Précédent
          </button>
          <span>Page {pagination.page} sur {pagination.totalPages}</span>
          <button
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Suivant
          </button>
        </div>
      )}

      {showCreateModal && (
        <TicketCreateModal
          token={token}
          user={user}
          userSubscription={userSubscription}
          currentView={currentView}
          previousView={previousView}
          selectedClientId={selectedClientId}
          selectedClientName={clients?.find(c => c.id === selectedClientId)?.name}
          selectedSiteId={selectedSiteId}
          selectedPlatform={currentDashboardType}
          globalConnectors={globalConnectors}
          userOrganizations={userOrganizations}
          onClose={() => setShowCreateModal(false)}
          onTicketCreated={handleTicketCreated}
        />
      )}
    </div>
  );
}

export default TicketsView;
